import { useCallback, useState } from 'react';
import type { GadgetData } from '@atlassian/jira-dashboard-common/src/types.tsx';
import { useConfigurationStatus } from '@atlassian/jira-dashboard-internal-common/src/controllers/configuration-status.tsx';
import type { UserPreferencesService } from '@atlassian/jira-dashboard-internal-common/src/services/user-preferences.tsx';
import { isConnectGadget } from '@atlassian/jira-dashboard-internal-common/src/utils/gadget-type.tsx';
import { lensReactGadget } from '@atlassian/jira-dashboard-internal-common/src/utils/react-gadget-lenses/index.tsx';
import { useGadgetTitle } from '@atlassian/jira-dashboard-internal-common/src/utils/use-gadget-title/index.tsx';
import type {
	ItemProperties,
	GadgetUserPreferences,
} from '@atlassian/jira-dashboard-user-preference/src/types.tsx';
import { useDashboardReactGadgetsResource } from '@atlassian/jira-router-resources-dashboard/src/index.tsx';

export type GadgetInstrumentation = Partial<{
	onEditModeEnter: () => Promise<void> | void;
}>;

export type GadgetEditableControllerArg = Pick<
	GadgetData,
	| 'id'
	| 'title'
	| 'customisedTitle'
	| 'configurable'
	| 'hasNonHiddenUserPrefs'
	| 'renderedGadgetUrl'
	| 'userPrefs'
	| 'amdModule'
	| 'reactKey'
> & {
	isRenamable: boolean;
	isConnectDisabled: boolean;
	saveTitle: (arg1: string) => Promise<void>;
	userPreferencesAction: GadgetUserPreferences['action'] | undefined;
	userPreferencesService: UserPreferencesService;
	instrumentation?: GadgetInstrumentation;
};

export const useGadgetEditableController = ({
	id,
	title: defaultTitle,
	customisedTitle,
	configurable,
	hasNonHiddenUserPrefs,
	renderedGadgetUrl,
	userPrefs,
	amdModule,
	reactKey,
	isRenamable,
	isConnectDisabled,
	saveTitle,
	userPreferencesAction,
	userPreferencesService,
	instrumentation,
}: GadgetEditableControllerArg) => {
	const { put: userPreferencesServicePut, refresh: userPreferencesServiceRefresh } =
		userPreferencesService;

	const { isConfigurable: isEditModeEnabled, isUnconfigured } = useConfigurationStatus({
		configurable,
		hasNonHiddenUserPrefs,
		renderedGadgetUrl,
		userPrefs,
	});
	const [isInEditMode, setIsInEditMode] = useState(isEditModeEnabled && isUnconfigured);
	const isEditButtonDisabled = isInEditMode && !isConnectGadget({ amdModule });

	const onEditModeTrigger = useCallback<() => void>(() => {
		instrumentation?.onEditModeEnter?.();
		setIsInEditMode(true);
	}, [setIsInEditMode, instrumentation]);

	const onEditModeCancel = useCallback<() => void>(() => setIsInEditMode(false), [setIsInEditMode]);

	const [isRenaming, setIsRenaming] = useState(false);

	const onRenameSave = useCallback(
		async (newTitle: string) => {
			if (isRenamable) {
				await saveTitle(newTitle);
			}
			setIsRenaming(false);
		},
		[isRenamable, saveTitle],
	);
	const { title, setDefaultTitle, setTitle } = useGadgetTitle({
		defaultTitle,
		title: customisedTitle,
		onSave: onRenameSave,
	});

	const onRenameTrigger = useCallback<() => void>(() => {
		setIsRenaming(true);
	}, [setIsRenaming]);

	const onRenameCancel = useCallback<() => void>(() => {
		setIsRenaming(false);
	}, [setIsRenaming]);

	const { update } = useDashboardReactGadgetsResource();

	const setPreferences = useCallback<
		(arg1: ItemProperties, arg2?: () => void) => Promise<void> | null
	>(
		(preferences, onUpdate) => {
			if (userPreferencesAction == null) {
				throw new Error('gadget attempted to save preference without existing preferences');
			}

			const maybePromise = userPreferencesServicePut(userPreferencesAction, preferences);
			if (!maybePromise) {
				return null;
			}
			return (async () => {
				try {
					await maybePromise;
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (e: any) {
					throw new Error(`failed to put gadget preference: ${e.message}`);
				}
				if (reactKey) {
					// refresh react gadget
					update(lensReactGadget(id, null));
				}

				const userPrefsFromService = await userPreferencesServiceRefresh(
					userPreferencesAction,
					onUpdate,
				);

				if (userPrefsFromService == null) {
					throw new Error('failed to get preferences after put preferences');
				}
			})();
		},
		[
			userPreferencesAction,
			userPreferencesServicePut,
			userPreferencesServiceRefresh,
			update,
			id,
			reactKey,
		],
	);

	return {
		isUnconfigured,
		isEditModeEnabled,
		isEditButtonDisabled,
		isInEditMode,
		isConnectDisabled,
		onEditModeTrigger,
		onEditModeCancel,
		title,
		isRenaming,
		setDefaultTitle,
		onRenameTrigger,
		onRenameSave: setTitle,
		onRenameCancel,
		setPreferences,
	};
};
